import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useParams } from "react-router";
import { axiosReq, axiosRes } from "../../api/axiosDefaults";
import { useRedirect } from "../../hooks/useRedirect";
import useUserStatus from "../../hooks/useUserStatus";
import Forbidden403 from "../errors/Forbidden403";
import styles from "../../styles/Admin.module.css";
import btnStyles from "../../styles/Button.module.css";
import useFetchShortTermOwners from "../../hooks/useFetchShortTermOwners";
import { useTranslation } from "react-i18next";

function ShortTermOwnerPage() {
    useRedirect("loggedOut");
    const { id } = useParams();
    const userStatus = useUserStatus();
    const { t } = useTranslation();
    const history = useHistory();

    const [ownerData, setOwnerData] = useState(null);
    const [show, setShow] = useState(false);
    const { owners } = useFetchShortTermOwners();

    useEffect(() => {
        const found = owners.find((o) => o.id === parseInt(id));
        if (found) setOwnerData(found);
    }, [owners, id]);

    const handleDeleteFile = async (fileId) => {
        const confirmDelete = window.confirm(t("admin.owner.confirmDelete"));
        if (!confirmDelete) return;
        try {
            await axiosReq.delete(
                `/short-term-listings/owners/${ownerData.id}/files/${fileId}/`
            );
            setOwnerData((prev) => ({
                ...prev,
                files: prev.files.filter((f) => f.id !== fileId),
            }));
        } catch (error) {
            console.error(t("admin.owner.errorDeletingFile"), error.response?.data || error.message);
        }
    };

    const handleDelete = async () => {
        try {
            await axiosRes.delete(`/short-term-listings/owners/${id}/`);
            history.push("/frontend/admin/short-term-listings/owners/");
        } catch (err) {
            console.error(err);
        }
    };

    if (!userStatus?.canAccessShortTerm) {
        return <Forbidden403 />;
    }

    const firstName = ownerData?.first_name;
    const displayName = firstName
        ? (firstName.endsWith("s") ? `${firstName}'` : `${firstName}'s`)
        : "";

    return (
        <Container className="mt-5 pt-5">
            <Row>
                <Col className="my-2">
                    <Card className="my-2">
                        <Card.Header>
                            <h1 className="text-center">{displayName} Info</h1>
                            <div className="d-flex justify-content-end">
                                <div className="me-auto">
                                    <Link to="/frontend/admin/short-term-listings/owners">
                                        <i className="fas fa-arrow-left" /> Back
                                    </Link>
                                </div>
                                <div>
                                    <Link to={`/frontend/admin/short-term-listings/owners/${id}/edit`}>
                                        <i className="fas fa-edit" />
                                    </Link>
                                </div>
                                {userStatus?.isSuperuser && (
                                    <div
                                        className="ms-4"
                                        style={{ cursor: "pointer", color: "#a00000" }}
                                        onClick={() => setShow(true)}
                                    >
                                        <i className="fas fa-trash" />
                                    </div>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    <Card.Title>
                                        {ownerData?.first_name} {ownerData?.last_name}
                                    </Card.Title>
                                    <Card.Text>
                                        {t("admin.owner.email")}: {ownerData?.email}
                                        <br />
                                        {t("admin.owner.phone")}: {ownerData?.phone}
                                        <br />
                                        {t("admin.owner.secondaryPhone")}: {ownerData?.phone_2}
                                        <br />
                                        {t("admin.owner.notes")}: {ownerData?.notes}
                                    </Card.Text>
                                    <h5>{t("admin.owner.uploadedFiles")}</h5>
                                    <ol className={styles.OwnerList}>
                                        {ownerData?.files?.length > 0 ? (
                                            ownerData.files.map((file, index) => (
                                                <li
                                                    key={index}
                                                    className="border rounded shadow p-1 mb-2 d-flex"
                                                    style={{ maxWidth: "100%" }}
                                                >
                                                    <a href={file.file} target="_blank" rel="noopener noreferrer">
                                                        {file.file_url}
                                                    </a>
                                                    {userStatus?.isSuperuser && (
                                                        <Button
                                                            className={`${btnStyles.Remove} ms-auto`}
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteFile(file.id)}
                                                        >
                                                            {t("admin.owner.delete")}
                                                        </Button>
                                                    )}
                                                </li>
                                            ))
                                        ) : (
                                            <li>{t("admin.owner.noFilesUploaded")}</li>
                                        )}
                                    </ol>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <Modal show={show} onHide={() => setShow(false)} centered size="md" className="text-center">
                    <Modal.Header closeButton className="text-dark border-dark" style={{ backgroundColor: "rgba(250, 140, 22, 0.85)" }}>
                        <Modal.Title>{t("admin.owner.deleteOwner")}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-dark" style={{ backgroundColor: "#fa8c16" }}>
                        <p className="h3">{t("admin.owner.confirmDeleteOwner")}</p>
                        <p className="h5">{t("admin.owner.cannotUndo")}</p>
                    </Modal.Body>
                    <Modal.Footer className="text-dark d-flex justify-content-center border-dark" style={{ backgroundColor: "rgba(250, 140, 22, 0.85)" }}>
                        <Button className={`${btnStyles.AngryOcean} ${btnStyles.Button}`} onClick={() => setShow(false)}>
                            {t("admin.owner.close")}
                        </Button>
                        <Button className={`${btnStyles.Remove} ${btnStyles.Button}`} onClick={handleDelete}>
                            {t("admin.owner.deleteOwner")}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Row>
        </Container>
    );
}

export default ShortTermOwnerPage;
